/** The words the family reads on the dashboard (skill 13 — no ledger vocabulary). */
import type { ChangeVerdict } from '@pfo/analytics';
import { isSamePeriod, type Period } from '@pfo/shared';

import { formatVnd } from '../../../shared/lib/money.js';

/** "Tháng này" for the month being lived, "Tháng 7/2026" for any other. */
export function periodLabel(period: Period, current: Period): string {
  return isSamePeriod(period, current) ? 'Tháng này' : `Tháng ${period.month}/${period.year}`;
}

/**
 * The comparison sentence, e.g. "Giảm mạnh 3.737.930đ so với cùng kỳ tháng trước".
 *
 * "Cùng kỳ" is load-bearing: for the running month the figure behind this comes from the same
 * number of days of the previous month, not the whole of it.
 */
export function changeSentence(
  verdict: ChangeVerdict,
  delta: number,
  /** 'Chi' or 'Thu' — which of the two tabs the sentence is describing. */
  subject: 'Chi' | 'Thu',
): string {
  if (verdict.direction === 'flat') {
    return 'Gần như không đổi so với cùng kỳ tháng trước';
  }

  const amount = formatVnd(Math.abs(delta));
  if (verdict.pct === null) {
    return verdict.direction === 'up'
      ? `${subject} ${amount}, cùng kỳ tháng trước chưa có khoản nào`
      : `Cùng kỳ tháng trước ${subject.toLowerCase()} ${amount}, tháng này chưa có khoản nào`;
  }

  const strength = verdict.intensity === 'strong' ? ' mạnh' : '';
  const verb = verdict.direction === 'up' ? 'Tăng' : 'Giảm';
  return `${verb}${strength} ${amount} so với cùng kỳ tháng trước`;
}

/**
 * Where the money went. These are the buckets of spec 9, in the words of decision A3's own
 * examples — "Tiết kiệm" is money put aside, "Dự phòng" is the emergency fund, and the two
 * are separate because the family treats them differently.
 */
export const ALLOCATION_LABELS = {
  expense: 'Chi tiêu',
  saving: 'Tiết kiệm',
  reserve: 'Quỹ dự phòng',
  investment: 'Đầu tư',
  debtPrincipal: 'Trả nợ gốc',
  paidForFamily: 'Chi cho gia đình',
  receivedFromFamily: 'Nhận từ gia đình',
  freeCash: 'Còn lại',
} as const;

/** How dependable a source of money is (decision B10). */
export const STABILITY_LABELS: Record<string, string> = {
  stable: 'Ổn định',
  semi_stable: 'Tương đối ổn định',
  variable: 'Biến động',
  one_off: 'Một lần',
};

/** Shown where a category is missing — an imported row, or a MoMo total. */
export const UNCATEGORISED_LABEL = 'Chưa phân loại';
